import { Screen } from "src/billinglatest/types";
import { useAppContext } from "src/hooks/useAppContext";
import { isUnderDevelopment } from "src/utils/featureFlag";

type LicenseCountProps = {
  used: number;
  total: number;
  type: string;
  isInTrial: boolean;
  remainingTrialDays: number | null;
};

const LicenseCount = ({
  used,
  total,
  type,
  isInTrial,
  remainingTrialDays,
}: LicenseCountProps) => {
  const testIdPrefix = type
    ? `${type.replace(/\s+/g, "-")}-license`
    : "standard-license";

  return (
    <span data-testid={`${testIdPrefix}-container`}>
      <strong data-testid={`${testIdPrefix}-used`}>{used}</strong> out of{" "}
      <strong data-testid={`${testIdPrefix}-total`}>{total}</strong>&nbsp;
      {isInTrial ? (
        <span data-testid={`${testIdPrefix}-trial-text`}>
          free {type} licenses are being used, active until your trial ends in{" "}
          <strong data-testid={`${testIdPrefix}-trial-days`}>
            {remainingTrialDays}
          </strong>{" "}
          days.
        </span>
      ) : (
        <span data-testid={`${testIdPrefix}-usage-text`}>
          {type} licenses are being used.
        </span>
      )}
    </span>
  );
};

const TrialMessage = () => (
  <span data-testid="trial-payment-message">
    To keep your paired screens after this date, please add your payment
    details.
  </span>
);

const LimitReachedMessage = ({ isTrialMode }: { isTrialMode: boolean }) => (
  <span data-testid="limit-reached-message">
    {isTrialMode
      ? "This additional trial screen won't be added to your organization pro rata for the current billing period until it's activated."
      : "This additional screen will be added to your organization pro rata for the current billing period."}
  </span>
);

type ScreenLicenseProps = {
  screenLicences: Screen | undefined;
  isInTrial: boolean;
  isTrialMode: boolean;
  remainingTrialDays: number | null;
};

export const ScreenLicense = ({
  screenLicences,
  isInTrial,
  isTrialMode,
  remainingTrialDays,
}: ScreenLicenseProps): JSX.Element | null => {
  if (!screenLicences) return null;

  const context = useAppContext();
  const standardLicenses = screenLicences;
  const standardLicensesLeft = standardLicenses.remaining;
  const hasReachedStandardLicensesLimit =
    standardLicensesLeft !== undefined && standardLicensesLeft <= 0;

  const mrlLicenses = screenLicences.mrl;
  const hasMrlLicenses = mrlLicenses.total > 0;
  const mrlLicensesLeft = mrlLicenses.remaining;
  const hasReachedMrlLicensesLimit =
    isUnderDevelopment(context) &&
    mrlLicensesLeft !== undefined &&
    mrlLicensesLeft <= 0 &&
    hasMrlLicenses;

  return (
    <div className="screen-license" data-testid="screen-license-container">
      <LicenseCount
        used={standardLicenses.active}
        total={standardLicenses.total}
        type=""
        isInTrial={isInTrial}
        remainingTrialDays={remainingTrialDays}
      />

      {isUnderDevelopment(context) && hasMrlLicenses && (
        <LicenseCount
          used={mrlLicenses.active}
          total={mrlLicenses.total}
          type="meeting room"
          isInTrial={isInTrial}
          remainingTrialDays={remainingTrialDays}
        />
      )}

      {isInTrial && <TrialMessage />}
      {!isInTrial &&
        (hasReachedStandardLicensesLimit || hasReachedMrlLicensesLimit) && (
          <LimitReachedMessage isTrialMode={isTrialMode} />
        )}
    </div>
  );
};
